/* assets/fh-geo-price.js
 *
 * Indicative local-currency prices for visitors outside the euro zone.
 *
 * The storefront quotes in EUR — enabledPresentmentCurrencies is ["EUR"],
 * because Shopify's native multi-currency needs Shopify Payments, which this
 * shop does not use. The payment provider settles the customer in their own
 * currency at its own rate, so the figure here is an orientation, not the
 * amount that lands on the statement. Hence the "≈" on every one of them, and
 * hence the footer line, which says who sets the final number and does not
 * claim a currency for it.
 *
 * Country comes from Shopify's own localisation (IP-based, set per market) via
 * window.FH_GEO_COUNTRY. Append ?fhc=US to any URL to force a country — useful
 * for checking how the shop reads from abroad.
 *
 * Struck-through compare-at prices are left alone. The theme draws the line
 * with a class rather than an <s> tag, so both are skipped explicitly; two
 * conversions stacked on one product card is noise, and the sale price is the
 * one the customer is deciding on.
 *
 * Rates are ECB euro reference rates, baked in rather than fetched, so there is
 * no third-party request in the page path. They drift a percent or two a month.
 * Re-bake them now and then.
 */
(function () {
  'use strict';

  var RATES_DATE = '2026-09-14';

  /* country -> [currency, rate (1 EUR = n), symbol, symbol after number, rounding step] */
  var MAP = {
    US: ['USD', 1.1551, '$', false, 1],
    CA: ['CAD', 1.6041, 'CA$', false, 1],
    GB: ['GBP', 0.85598, '£', false, 1],
    CH: ['CHF', 0.9431, 'CHF ', false, 1],
    NO: ['NOK', 10.767, ' kr', true, 10],
    SE: ['SEK', 11.281, ' kr', true, 10],
    DK: ['DKK', 7.4753, ' kr', true, 5],
    PL: ['PLN', 4.3418, ' zł', true, 5],
    CZ: ['CZK', 24.294, ' Kč', true, 10],
    AU: ['AUD', 1.6202, 'A$', false, 1],
    NZ: ['NZD', 2.0012, 'NZ$', false, 1],
    JP: ['JPY', 178.52, '¥', false, 100],
    HK: ['HKD', 9.0599, 'HK$', false, 1],
    SG: ['SGD', 1.4676, 'S$', false, 1],
    KR: ['KRW', 1555.04, '₩', false, 100],
    IL: ['ILS', 3.527, '₪', false, 1],
    MY: ['MYR', 4.7082, 'RM', false, 1],
    AE: ['AED', 4.2421, 'AED ', false, 1]
  };

  function country() {
    try {
      var m = (window.location.search || '').match(/[?&]fhc=([A-Za-z]{2})/);
      if (m) return m[1].toUpperCase();
    } catch (_e) {}
    return (window.FH_GEO_COUNTRY || '').toUpperCase();
  }

  var C = MAP[country()];
  if (!C) return; /* euro zone, or somewhere we do not quote */

  var CODE = C[0], RATE = C[1], SYM = C[2], AFTER = C[3], STEP = C[4];

  var SKIP = '[data-fh-noconv], s, del, .compare-at-price, [class*="compare-at"], [class*="price--compare"]';

  /* Matches the shop's euro prices: €95,00, € 1.234,56, €1,234.56 */
  var MONEY = /€\s?(\d{1,3}(?:[.,]\d{3})*(?:[.,]\d{1,2})?)/;

  function toNumber(s) {
    var t = s.replace(/\s/g, '');
    /* A comma one or two digits from the end is a decimal comma; otherwise
       commas are thousands separators. */
    if (/,\d{1,2}$/.test(t)) t = t.replace(/\./g, '').replace(',', '.');
    else t = t.replace(/,/g, '');
    var n = parseFloat(t);
    return isFinite(n) ? n : null;
  }

  function format(eur) {
    var v = eur * RATE;
    v = Math.round(v / STEP) * STEP;
    var s = v.toLocaleString('en-US');
    return AFTER ? s + SYM : SYM + s;
  }

  var busy = false;

  function decorate(root) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (n) {
        if (!n.nodeValue || n.nodeValue.indexOf('€') === -1) return NodeFilter.FILTER_REJECT;
        var p = n.parentNode;
        if (!p || p.nodeType !== 1) return NodeFilter.FILTER_REJECT;
        var tag = p.tagName;
        if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'TEXTAREA' || tag === 'OPTION') {
          return NodeFilter.FILTER_REJECT;
        }
        if (p.classList && p.classList.contains('fh-conv')) return NodeFilter.FILTER_REJECT;
        if (p.closest && p.closest(SKIP)) return NodeFilter.FILTER_REJECT;
        return MONEY.test(n.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      }
    });

    var hits = [], n;
    while ((n = walker.nextNode())) hits.push(n);

    hits.forEach(function (node) {
      var m = node.nodeValue.match(MONEY);
      if (!m) return;
      var eur = toNumber(m[1]);
      if (eur === null || eur <= 0) return;

      var span = document.createElement('span');
      span.className = 'fh-conv';
      span.textContent = ' ≈ ' + format(eur);
      span.title = 'Approximate. ' + m[0] + ' converted at the ECB rate of ' + RATES_DATE +
                   '. Your payment provider sets the exact amount at checkout.';
      node.parentNode.insertBefore(span, node.nextSibling);
    });
  }

  function clear(root) {
    var old = root.querySelectorAll('.fh-conv');
    for (var i = 0; i < old.length; i++) old[i].parentNode.removeChild(old[i]);
  }

  function note() {
    if (document.getElementById('fh-conv-note')) return;
    var host = document.querySelector('footer') || document.body;
    var p = document.createElement('p');
    p.id = 'fh-conv-note';
    p.setAttribute('data-fh-noconv', '');
    p.textContent =
      'Prices are listed in euro. Figures in ' + CODE +
      ' are indicative, converted at the European Central Bank rate of ' + RATES_DATE +
      '. The exact amount is set by your payment provider at checkout.';
    host.appendChild(p);
  }

  function run() {
    if (busy) return;
    busy = true;
    try {
      clear(document.body);
      decorate(document.body);
      note();
    } catch (_e) {}
    /* Let the observer settle before listening again, so our own inserts
       do not retrigger us. */
    setTimeout(function () { busy = false; }, 60);
  }

  function style() {
    var css =
      '.fh-conv{opacity:.62;font-size:.88em;white-space:nowrap}' +
      '#fh-conv-note{margin:0;padding:14px clamp(16px,4vw,40px) 22px;text-align:center;' +
      'font-size:11px;line-height:1.6;opacity:.55;font-family:var(--fr-sans,"Inter",system-ui,sans-serif)}';
    var s = document.createElement('style');
    s.appendChild(document.createTextNode(css));
    document.head.appendChild(s);
  }

  function start() {
    style();
    run();

    var timer = null;
    var obs = new MutationObserver(function () {
      if (busy) return;
      clearTimeout(timer);
      timer = setTimeout(run, 150);
    });
    obs.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
